import { Suspense, useCallback, useDeferredValue, useMemo, useState } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import {
    BlockStack,
    Box,
    Button,
    Card,
    InlineStack,
    PageHeader,
    SegmentedControl,
    TextField,
    Typography,
    type SegmentedOption,
} from '@pv-frontend/ui-components-web';
import { experimentsScreenQuery } from '../relay/experiments-query';
import type { experimentsQuery as ExperimentsQuery } from '../relay/__generated__/experimentsQuery.graphql';
import { vaultSearchQuery } from '../relay/vault-search-query';
import type { vaultSearchQuery as VaultSearchQuery } from '../relay/__generated__/vaultSearchQuery.graphql';
import { useExperimentActions } from '../relay/use-experiment-actions';
import { MeasurementPendingNotice } from './measurement-pending-notice';
import { DefineExperimentForm } from './define-experiment-form';
import { ExperimentList, type ExperimentView } from './experiment-list';
import { VaultCohortPicker, type VaultOptionView } from './vault-cohort-picker';
import { LoadingContent } from './loading-content';

/**
 * The experiment console (slice 3 of PRD #11): define an experiment over a flag,
 * enroll a vault cohort using the tenant pillar's vault search, and list every
 * defined experiment with its cohort. A prominent measurement-pending notice
 * caps the screen — pv-data has no exposure-event consumer yet, so cohorts can be
 * staged but nothing is measured.
 *
 * `fetchKey` bumps after each successful mutation to refetch the `experiments`
 * list (the mutation payloads can't reconcile the root connection). The base data
 * stays in the store, so the refetch reads through rather than re-suspending.
 */
export function ExperimentsScreen() {
    const [fetchKey, setFetchKey] = useState(0);
    const data = useLazyLoadQuery<ExperimentsQuery>(
        experimentsScreenQuery,
        {},
        { fetchPolicy: 'store-and-network', fetchKey },
    );

    const refresh = useCallback(() => setFetchKey((key) => key + 1), []);
    const { defining, enrolling, onDefine, onEnroll } = useExperimentActions(refresh);

    const experiments: ExperimentView[] = data.experiments.map((experiment) => ({
        key: experiment.key,
        flagKey: experiment.flagKey,
        variants: experiment.variants.map((variant) => ({ name: variant.name, weight: variant.weight })),
        assignments: experiment.assignments.map((assignment) => ({
            vaultId: assignment.vaultId,
            variant: assignment.variant,
            assignedUtc: assignment.assignedUtc,
            firstExposedUtc: assignment.firstExposedUtc ?? null,
        })),
    }));

    const flagKeys = data.featureFlags.map((flag) => flag.key);
    const existingKeys = experiments.map((experiment) => experiment.key);

    return (
        <Box padding="600" maxWidth={1080}>
            <BlockStack gap="500">
                <PageHeader
                    title="Experiments"
                    description="Define A/B experiments over a flag and enroll vault cohorts from the Admin API."
                />

                <MeasurementPendingNotice />

                <DefineExperimentForm
                    flagKeys={flagKeys}
                    existingKeys={existingKeys}
                    busy={defining}
                    onDefine={onDefine}
                />

                <EnrollCohortPanel
                    experimentKeys={existingKeys}
                    enrolling={enrolling}
                    onEnroll={onEnroll}
                />

                <BlockStack gap="300">
                    <Typography variant="heading-sm" as="h2">
                        Defined experiments
                    </Typography>
                    <ExperimentList experiments={experiments} />
                </BlockStack>
            </BlockStack>
        </Box>
    );
}

interface EnrollCohortPanelProps {
    experimentKeys: readonly string[];
    enrolling: boolean;
    onEnroll: (experimentKey: string, vaultIds: readonly string[], onSuccess?: () => void) => void;
}

/**
 * Assemble and enroll a vault cohort: pick the target experiment, search vaults,
 * and toggle them in or out. The vault-search query sits behind its own Suspense
 * boundary so the search field keeps focus as results re-fetch on each keystroke;
 * the selection lives here so it survives those re-searches.
 */
function EnrollCohortPanel({ experimentKeys, enrolling, onEnroll }: EnrollCohortPanelProps) {
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [selected, setSelected] = useState<ReadonlyMap<string, VaultOptionView>>(() => new Map());
    const [search, setSearch] = useState('');
    // The server query trails the live input so results don't flash the loading state.
    const deferredSearch = useDeferredValue(search);

    // Keep the target valid as experiments come and go; default to the first.
    const effectiveKey =
        selectedKey && experimentKeys.includes(selectedKey) ? selectedKey : (experimentKeys[0] ?? null);

    const experimentOptions = useMemo<SegmentedOption<string>[]>(
        () => experimentKeys.map((key) => ({ value: key, label: key })),
        [experimentKeys],
    );

    const toggle = useCallback((vault: VaultOptionView) => {
        setSelected((current) => {
            const next = new Map(current);
            if (next.has(vault.vaultId)) next.delete(vault.vaultId);
            else next.set(vault.vaultId, vault);
            return next;
        });
    }, []);

    const selectedIds = useMemo(() => new Set(selected.keys()), [selected]);

    const enroll = () => {
        if (!effectiveKey || selected.size === 0) return;
        onEnroll(effectiveKey, [...selected.keys()], () => setSelected(new Map()));
    };

    if (experimentKeys.length === 0) {
        return (
            <Card>
                <Box padding="400">
                    <BlockStack gap="100">
                        <Typography variant="heading-sm" as="h2">
                            Enroll a cohort
                        </Typography>
                        <Typography variant="body-sm" tone="text-secondary">
                            Define an experiment first, then enroll a vault cohort into it.
                        </Typography>
                    </BlockStack>
                </Box>
            </Card>
        );
    }

    return (
        <Card>
            <Box padding="400">
                <BlockStack gap="400">
                    <Typography variant="heading-sm" as="h2">
                        Enroll a cohort
                    </Typography>

                    <BlockStack gap="150">
                        <Typography variant="body-sm" fontWeight="medium" tone="text-secondary">
                            Experiment
                        </Typography>
                        <SegmentedControl
                            accessibilityLabel="Experiment to enroll into"
                            options={experimentOptions}
                            value={effectiveKey ?? ''}
                            disabled={enrolling}
                            onChange={setSelectedKey}
                        />
                    </BlockStack>

                    <TextField
                        value={search}
                        onChange={setSearch}
                        accessibilityLabel="Search vaults by name, identifier or owner email"
                        placeholder="Search vaults to enroll…"
                        type="search"
                        leadingIcon="search"
                        disabled={enrolling}
                    />

                    {selected.size > 0 ? (
                        <BlockStack gap="150">
                            <Typography variant="body-sm" fontWeight="medium" tone="text-secondary">
                                {selected.size} selected
                            </Typography>
                            <InlineStack gap="150" blockAlign="center" wrap>
                                {[...selected.values()].map((vault) => (
                                    <Button
                                        key={vault.vaultId}
                                        size="inline"
                                        variant="tertiary"
                                        disabled={enrolling}
                                        onPress={() => toggle(vault)}
                                        testID={`chip-${vault.vaultId}`}
                                    >
                                        {`${vault.name}  ✕`}
                                    </Button>
                                ))}
                            </InlineStack>
                        </BlockStack>
                    ) : null}

                    <Suspense fallback={<LoadingContent />}>
                        <CohortResults query={deferredSearch} search={search} selectedIds={selectedIds} onToggle={toggle} />
                    </Suspense>

                    <Box>
                        <Button
                            variant="primary"
                            size="slim"
                            disabled={enrolling || selected.size === 0}
                            isLoading={enrolling}
                            onPress={enroll}
                        >
                            {selected.size === 0
                                ? 'Enroll cohort'
                                : `Enroll ${selected.size} ${selected.size === 1 ? 'vault' : 'vaults'}`}
                        </Button>
                    </Box>
                </BlockStack>
            </Box>
        </Card>
    );
}

interface CohortResultsProps {
    query: string;
    search: string;
    selectedIds: ReadonlySet<string>;
    onToggle: (vault: VaultOptionView) => void;
}

/** Fetches the vault-search results for the (deferred) query and hands them to the picker. */
function CohortResults({ query, search, selectedIds, onToggle }: CohortResultsProps) {
    const data = useLazyLoadQuery<VaultSearchQuery>(
        vaultSearchQuery,
        { query, filter: 'ALL' },
        { fetchPolicy: 'store-or-network' },
    );

    const vaults: VaultOptionView[] = data.vaultSearch.map((vault) => ({
        vaultId: vault.vaultId,
        name: vault.name,
        identifier: vault.identifier,
        ownerEmail: vault.ownerEmail ?? null,
    }));

    return <VaultCohortPicker vaults={vaults} selectedIds={selectedIds} onToggle={onToggle} search={search} />;
}
